/**
 * University-tab card taxonomy — 4 reusable shells matching
 * `/app/design_guidelines.json`. Every University tab component
 * should compose these instead of raw shadcn <Card> to get the
 * editorial rhythm for free.
 *
 * HeroCard      — dominant metric with thick navy top-border
 * ChartCard     — SVG chart wrapper with header row + optional legend
 * DataListCard  — border-only card with hover-highlighted rows
 * ProseCard     — pull-quote card with left purple accent + serif body
 *
 * All shells accept an optional `data-testid` prop and forward it
 * verbatim — never invent new testids for a component you're wrapping.
 */

export function TabKicker({ children, className = "" }) {
  return (
    <span
      className={`font-mono text-xs uppercase tracking-[0.2em] text-[#7C3AED] mb-2 block ${className}`}
    >
      {children}
    </span>
  );
}

export function TabSectionTitle({ children, className = "" }) {
  return (
    <h2
      className={`font-serif text-2xl md:text-3xl text-[#002147] leading-tight border-b border-[#002147]/10 pb-4 mb-6 ${className}`}
    >
      {children}
    </h2>
  );
}

export function HeroCard({
  metric, subtitle, kicker, prose,
  className = "", metricClassName = "",
  ...props
}) {
  return (
    <div
      className={`flex flex-col p-6 md:p-8 border-t-4 border-[#002147] bg-[#F3F1EC] shadow-none rounded-none ${className}`}
      {...props}
    >
      {kicker && <TabKicker>{kicker}</TabKicker>}
      <div className={`text-5xl md:text-6xl font-serif text-[#002147] mb-2 leading-none ${metricClassName}`}>
        {metric}
      </div>
      {subtitle && (
        <div className="font-mono text-sm uppercase tracking-widest text-[#002147]/60">
          {subtitle}
        </div>
      )}
      {prose && (
        <div className="mt-4 text-sm font-sans text-[#002147]/80 border-t border-[#002147]/10 pt-4 leading-relaxed">
          {prose}
        </div>
      )}
    </div>
  );
}

export function ChartCard({
  title, legend, children, className = "", headerRight, ...props
}) {
  return (
    <div
      className={`p-6 bg-white/50 border border-[#002147]/10 rounded-sm relative overflow-hidden ${className}`}
      {...props}
    >
      {(title || legend || headerRight) && (
        <div className="flex justify-between items-baseline mb-6 border-b border-[#002147]/10 pb-4">
          <div>
            {title && (
              <h3 className="font-sans font-semibold text-lg text-[#002147]">{title}</h3>
            )}
            {legend && (
              <div className="font-mono text-xs text-[#002147]/60 flex gap-4 mt-1">
                {legend}
              </div>
            )}
          </div>
          {headerRight}
        </div>
      )}
      {children}
    </div>
  );
}

export function DataListCard({ title, className = "", children, ...props }) {
  return (
    <div
      className={`flex flex-col rounded-sm border border-[#002147]/10 bg-transparent ${className}`}
      {...props}
    >
      {title && (
        <div className="font-mono text-xs uppercase tracking-widest text-[#002147]/60 px-4 pt-4 pb-2">
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

export function DataRow({ label, value, emphasis = false, sublabel = null }) {
  return (
    <div
      className={`flex justify-between items-center py-3 px-4 border-b border-[#002147]/10 last:border-0 hover:bg-[#002147]/[0.02] transition-colors ${
        emphasis ? "border-t-2 !border-t-[#002147]" : ""
      }`}
    >
      <div className={`font-sans text-sm ${emphasis ? "font-semibold text-[#002147]" : "text-[#002147]/80"}`}>
        {label}
        {sublabel && (
          <div className="mt-0.5 text-[10px] font-mono text-stone-500 leading-tight" title={sublabel}>
            source · {sublabel}
          </div>
        )}
      </div>
      <div className={`font-mono text-sm font-medium text-right ${emphasis ? "text-lg text-[#002147]" : "text-[#002147]"}`}>
        {value}
      </div>
    </div>
  );
}

export function ProseCard({ children, className = "" }) {
  return (
    <div className={`p-6 md:p-8 bg-[#002147]/[0.02] border-l-2 border-[#7C3AED] relative ${className}`}>
      <span className="absolute top-4 left-4 text-6xl font-serif text-[#7C3AED]/10 select-none leading-none">"</span>
      <div className="relative font-serif text-lg md:text-xl leading-relaxed text-[#002147]">
        {children}
      </div>
    </div>
  );
}

/**
 * TabSplit — two-column tab layout used across the writeup-bearing
 * tabs (Admission / Cost / Life / Teaching / Overview).
 *
 * Left column (1/3 on desktop) hosts the editorial writeup — a reading
 * rail that anchors the tab. Right column (2/3) hosts the infographics
 * and data cards that used to stack below the writeup.
 *
 * Both columns scroll independently by default. On mobile the layout
 * collapses to a single column with the writeup on top.
 */
export function TabSplit({ left, right, className = "" }) {
  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 ${className}`}
      data-testid="tab-split"
    >
      <aside className="min-w-0">
        {left}
      </aside>
      <div className="min-w-0 space-y-8">
        {right}
      </div>
    </div>
  );
}
